import * as XLSX from 'xlsx';
import { InspectionRequest, InspectionItemPayload, InspectionExportRow } from '@/services/types/inspection.types';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { INSPECTION_ITEM_STATUS_MAP } from '@/shared/constants/statusMaps';
import dayjs from 'dayjs';


const formatDate = (dateString?: string) => {
  if (!dateString) return null;
  return dayjs(dateString).format('DD/MM/YYYY');
};


const formatTime = (dateString?: string) => {
  if (!dateString) return null;
  return dayjs(dateString).format('HH:mm:ss');
};


const formatDateTime = (dateString?: string) => {
  if (!dateString) return null;
  return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
};

// Formatar CPF: XXX.XXX.XXX-XX
const formatCPF = (cpf?: string) => {
  if (!cpf) return "";
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

// Formatar CNPJ: XX.XXX.XXX/XXXX-XX
const formatCNPJ = (cnpj?: string) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

// Formatar CPF ou CNPJ automaticamente
const formatCPFCNPJ = (doc?: string) => {
  if (!doc) return "";
  const cleaned = doc.replace(/\D/g, '');
  if (cleaned.length === 11) return formatCPF(doc);
  if (cleaned.length === 14) return formatCNPJ(doc);
  return doc;
};

// Formatar Telefone: +55 (XX) XXXXX-XXXX ou +55 (XX) XXXX-XXXX
const formatPhone = (phone?: string) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, '');

  // Remove código do país 55 se já existir
  const numberOnly = cleaned.startsWith('55') && cleaned.length > 11 ? cleaned.substring(2) : cleaned;

  if (numberOnly.length === 11) {
    // Celular: +55 (XX) XXXXX-XXXX
    return numberOnly.replace(/(\d{2})(\d{5})(\d{4})/, '+55 ($1) $2-$3');
  }
  if (numberOnly.length === 10) {
    // Fixo: +55 (XX) XXXX-XXXX
    return numberOnly.replace(/(\d{2})(\d{4})(\d{4})/, '+55 ($1) $2-$3');
  }
  return phone;
};

// Formatar CEP: apenas números (sem formatação)
const formatCEP = (cep?: string) => {
  if (!cep) return "";
  return cep.replace(/\D/g, '');
};

// Mapear timeSlot para descrição em português
const getTimeSlotDescription = (timeSlot?: number) => {
  if (!timeSlot) return null;
  const timeSlotMap: { [key: number]: string } = {
    1: 'Entre 08h e 12h',
    2: 'Entre 12h e 18h',
    3: 'Entre 18h e 22h',
  };
  return timeSlotMap[timeSlot] || null;
};

export const mapInspectionToExportRow = (request: InspectionRequest, item: InspectionItemPayload) => {
  const proponent = request.proponents?.[0];
  const schedule = item.schedules?.[0];

  // Buscar o último assignment ativo (não cancelado)
  const assignments = item.assignments || [];
  const lastAssignment = assignments.length > 0 ? assignments[assignments.length - 1] : null;

  // Workflow completo ordenado cronologicamente
  const workflow = item.workflow?.statusWorkflow || [];
  const sortedWorkflow = [...workflow].sort((a, b) =>
    new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
  );

  // Buscar eventos importantes do workflow
  // Para laudos que podem ser devolvidos e reaprovados, pegamos sempre a ÚLTIMA ocorrência
  const laudosAceitos = sortedWorkflow.filter(w => Number(w.statusCode) === InspectionItemStatus.LaudoAceitoPeloBanco);
  const statusLaudoAceito = laudosAceitos.length > 0 ? laudosAceitos[laudosAceitos.length - 1] : null;

  const statusVistoriaRealizada = sortedWorkflow.find(w => Number(w.statusCode) === InspectionItemStatus.AguardandoGeracaoLaudo);
  const statusAguardandoAceite = sortedWorkflow.find(w => Number(w.statusCode) === InspectionItemStatus.AguardandoAceitePrestadora);

  // Capturar TODAS as formas de entrega/aprovação de laudo (sempre a mais recente)
  const allLaudoEvents = sortedWorkflow.filter(w => {
    const status = Number(w.statusCode);
    return status === InspectionItemStatus.AguardandoRevisaoBanco ||     // Prestadora envia para banco
           status === InspectionItemStatus.LaudoAutomaticoGerado ||       // IA gera laudo automático
           status === InspectionItemStatus.AguardandoRevisaoPrestadora || // Aguardando revisão prestadora
           status === InspectionItemStatus.LaudoAceitoPeloBanco;          // Banco aprova laudo (final)
  });
  const firstSubmission = allLaudoEvents.length > 0 ? allLaudoEvents[0] : null;
  const lastSubmission = allLaudoEvents.length > 0 ? allLaudoEvents[allLaudoEvents.length - 1] : null;

  // Acessar dados do inspetor corretamente
  const inspectorName = lastAssignment?.inspectorName || "";
  const inspectorCpf = lastAssignment?.inspectorCpf || "";

  // Formatar telefones dos proponentes
  const formattedPhones = proponent?.phones?.map(p => formatPhone(p.phoneNumber)).join(' | ') || "";

  return {
    "ID": request.id || item.id || "",
    "Criação": formatDate(item.createdAt),
    "Hora Criação": formatTime(item.createdAt),
    "Data Entrega Laudo": formatDateTime(lastSubmission?.occurredAt) || null,
    "Base": "Banco Bari",
    "OP": request.proposalNumber || "",
    "-": "", // Coluna vazia intencional
    "Tipo": item.inspectionTypeName || "",
    "Finalidade": item.inspectionReasonName || "",
    "Proponentes": proponent?.name || "",
    "CPF/CNPJ Proponente": formatCPFCNPJ(proponent?.cpfCnpj) || "",
    "Telefone(s) Proponente": formattedPhones,
    "Tipo do imóvel": item.propertyTypeName || "",
    "Logradouro": item.street || "",
    "Número": item.number || "",
    "Complemento": item.complement || "",
    "CEP": formatCEP(item.cep),
    "Bairro": item.district || "",
    "Município": item.city || "",
    "UF": item.state || "",
    "N° IPTU": item.iptuNumber || "",
    "N° Cartório": item.registryOfficeNumber || "",
    "Área privativa (m²)": item.privateArea !== undefined && item.privateArea !== null ? item.privateArea : "",
    "Área construída (m²)": item.builtArea !== undefined && item.builtArea !== null ? item.builtArea : "",
    "Tipo de imóvel": item.propertyTypeName || "",
    "Área terreno (m²)": item.landArea !== undefined && item.landArea !== null ? item.landArea : "",
    "N° Matrícula": item.registrationNumber || "",
    "Status": item.statusDescription || "",
    "Data Status": formatDateTime(sortedWorkflow[sortedWorkflow.length - 1]?.occurredAt) || formatDateTime(request.updatedAt),
    "Prestadora": lastAssignment?.providerName || "",
    "CPF Inspetor": formatCPF(inspectorCpf),
    "Inspetor": inspectorName,
    "Data Agendamento": formatDate(schedule?.scheduleDate) || null,
    "Horário Agendamento": getTimeSlotDescription(schedule?.timeSlot) || null,
    "Data Vistoria": formatDate(statusVistoriaRealizada?.occurredAt) || null,
    "Analista Responsável Prestadora": lastAssignment?.providerAssignedUserName || "",
    "Dt. Recebimento Prest.": formatDateTime(statusAguardandoAceite?.occurredAt) || null
  };
};

/**
 * Mapeia uma linha achatada do endpoint /export para as colunas do Excel — as datas de
 * workflow, o direcionamento atual (Prestadora/Inspetor), o elaborador do laudo e o
 * agendamento já vêm resolvidos do backend, com o MESMO critério dos filtros do modal.
 */
export const mapExportRowToExcelRow = (row: InspectionExportRow) => ({
  "ID": row.requestId || row.itemId || "",
  "Criação": formatDate(row.createdAt),
  "Hora Criação": formatTime(row.createdAt),
  "Data Entrega Laudo": formatDateTime(row.reportDeliveredAt ?? undefined) || null,
  "Base": "Banco Bari",
  "OP": row.proposalNumber || "",
  "-": "", // Coluna vazia intencional
  "Tipo": row.inspectionTypeName || "",
  "Finalidade": row.inspectionReasonName || "",
  "Proponentes": row.proponentName || "",
  "CPF/CNPJ Proponente": formatCPFCNPJ(row.proponentCpfCnpj ?? undefined) || "",
  "Telefone(s) Proponente": (row.proponentPhones || []).map(p => formatPhone(p)).join(' | ') || "",
  "Tipo do imóvel": row.propertyTypeName || "",
  "Logradouro": row.street || "",
  "Número": row.number || "",
  "Complemento": row.complement || "",
  "CEP": formatCEP(row.cep ?? undefined),
  "Bairro": row.district || "",
  "Município": row.city || "",
  "UF": row.state || "",
  "N° IPTU": row.iptuNumber || "",
  "N° Cartório": row.registryOfficeNumber || "",
  "Área privativa (m²)": row.privateArea !== undefined && row.privateArea !== null ? row.privateArea : "",
  "Área construída (m²)": row.builtArea !== undefined && row.builtArea !== null ? row.builtArea : "",
  "Tipo de imóvel": row.propertyTypeName || "",
  "Área terreno (m²)": row.landArea !== undefined && row.landArea !== null ? row.landArea : "",
  "N° Matrícula": row.registrationNumber || "",
  // Mesmo rótulo do dropdown "Status" do modal (e do restante da tela), para o que o
  // usuário filtrou ser exatamente o que ele lê na coluna.
  "Status": INSPECTION_ITEM_STATUS_MAP[row.statusCode as InspectionItemStatus]?.label || row.statusDescription || "",
  "Data Status": formatDateTime(row.lastStatusAt ?? undefined) || formatDateTime(row.requestUpdatedAt ?? undefined),
  "Prestadora": row.providerName || "",
  "CPF Inspetor": formatCPF(row.inspectorCpf ?? undefined),
  "Inspetor": row.inspectorName || "",
  "Data Agendamento": formatDate(row.scheduleDate ?? undefined) || null,
  "Horário Agendamento": getTimeSlotDescription(row.scheduleTimeSlot ?? undefined) || null,
  "Data Vistoria": formatDate(row.inspectionDoneAt ?? undefined) || null,
  "Analista Responsável Prestadora": row.providerAssignedUserName || "",
  "Dt. Recebimento Prest.": formatDateTime(row.providerReceivedAt ?? undefined) || null,
  // Colunas novas SEMPRE no final: o layout do Panorama é consumido por posição, então
  // nenhuma coluna existente pode mudar de lugar.
  // Quem transmitiu o laudo — mesmos usuários que o filtro "Elaborador do Laudo" compara,
  // para o usuário conseguir conferir o resultado do filtro na própria planilha.
  "Elaborador do Laudo": row.reportAuthorName || "",
  "Laudo Retransmitido por": row.reportOtherTransmittersNames || "",
  // Uma linha por imóvel: solicitações com vários imóveis repetem o ID da proposta,
  // então o ID do item é o que distingue as linhas (não são duplicatas).
  "ID Item": row.itemId || "",
  // Squad da proposta no Salesforce (vazio para solicitações de outros canais)
  "Squad": row.squad || "",
});

/** Gera o Excel do Panorama a partir das linhas achatadas do endpoint /export. */
export const exportRowsToExcel = (rows: InspectionExportRow[], fileName: string = 'PANORAMA_AVALIACOES.xlsx') => {
  const excelRows = rows.map(mapExportRowToExcelRow);

  const worksheet = XLSX.utils.json_to_sheet(excelRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Dados - Inspeções");

  XLSX.writeFile(workbook, fileName);
};

export const exportInspectionsToExcel = (requests: InspectionRequest[], fileName: string = 'PANORAMA_AVALIACOES.xlsx') => {
  const rows: any[] = [];

  requests.forEach(request => {
    request.items.forEach(item => {
      rows.push(mapInspectionToExportRow(request, item));
    });
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Dados - Inspeções");

  XLSX.writeFile(workbook, fileName);
};
